// Scheduled email digests (weekly / monthly): list, create/edit, preview
// (sandboxed HTML + CSV download), send now, delete. Works with or without
// a portal — without one it's a project-level digest.

import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    IconButton,
    MenuItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete, Download, Edit, Send, Visibility } from '@mui/icons-material'

import {
    useCreateScheduledReportMutation,
    useDeleteScheduledReportMutation,
    useListScheduledReportsQuery,
    useSendScheduledReportMutation,
    useUpdateScheduledReportMutation,
} from '@/apis/v2Api'
import {
    digestCsvFilename,
    lastRunColor,
    parseEmailList,
    sendResultMessage,
    type DigestFrequency,
    type DigestRunResult,
    type Portal,
    type ScheduledReport,
} from '@/apis/portalTypes'
import type { ProjectConfigResponse } from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useProjectId } from '@/hooks/useReportArgs'
import { downloadText } from '@/utils/download'
import { ReportStatus, SectionCard, fmtDateTime } from '@/modules/reports/common'

import { ApiErrorAlert } from './ApiErrorAlert'
import { SubTenantSinglePicker } from './SubTenantPicker'

type Entitlements = ProjectConfigResponse['entitlements'] | undefined

function ScheduleDialog({
    report,
    portals,
    defaultPortalId,
    ent,
    onClose,
}: {
    report: ScheduledReport | null
    portals: Portal[]
    defaultPortalId: string | null
    ent: Entitlements
    onClose: () => void
}) {
    const projectId = useProjectId()
    const [name, setName] = useState(report?.name ?? '')
    const [frequency, setFrequency] = useState<DigestFrequency>(report?.frequency ?? 'weekly')
    const [portalId, setPortalId] = useState<string>(
        report ? (report.portal_id ?? '') : (defaultPortalId ?? ''),
    )
    const [subTenant, setSubTenant] = useState<string | null>(report?.sub_tenant ?? null)
    const [recipientsRaw, setRecipientsRaw] = useState((report?.recipients ?? []).join(', '))
    const [includeCsv, setIncludeCsv] = useState(report?.include_csv ?? false)
    const [active, setActive] = useState(report?.active ?? true)
    const [create, createState] = useCreateScheduledReportMutation()
    const [update, updateState] = useUpdateScheduledReportMutation()

    const csvAllowed = ent?.reportCsv !== false
    const maxRecipients = ent?.maxReportRecipients ?? null
    const { emails, invalid } = parseEmailList(recipientsRaw)
    const tooMany = maxRecipients !== null && emails.length > maxRecipients
    const valid = emails.length > 0 && invalid.length === 0 && !tooMany
    const busy = createState.isLoading || updateState.isLoading

    const save = async () => {
        const body = {
            name: name.trim(),
            frequency,
            portal_id: portalId || null,
            sub_tenant: subTenant,
            recipients: emails,
            include_csv: csvAllowed && includeCsv,
            active,
        }
        try {
            if (report) {
                await update({ projectId, id: report.id, ...body }).unwrap()
            } else {
                await create({ projectId, ...body }).unwrap()
            }
            onClose()
        } catch {
            // shown below
        }
    }

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{report ? 'Edit scheduled report' : 'New scheduled report'}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2 }}>
                <ApiErrorAlert error={createState.error ?? updateState.error} />
                <TextField
                    size="small"
                    label="Name"
                    placeholder="Weekly performance"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                    sx={{ mt: 1 }}
                />
                <TextField
                    size="small"
                    select
                    label="Frequency"
                    value={frequency}
                    onChange={e => setFrequency(e.target.value as DigestFrequency)}
                    helperText="Sent at 06:00 in the project's timezone — weekly on Mondays (last week), monthly on the 1st (last month)."
                >
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                </TextField>
                <TextField
                    size="small"
                    select
                    label="Branding"
                    value={portalId}
                    onChange={e => setPortalId(e.target.value)}
                    helperText="With a portal, the email uses its branding and links to it."
                >
                    <MenuItem value="">None (project-level digest)</MenuItem>
                    {portals.map(p => (
                        <MenuItem key={p.id} value={p.id}>
                            {p.branding.productName} ({p.slug})
                        </MenuItem>
                    ))}
                </TextField>
                <SubTenantSinglePicker
                    projectId={projectId}
                    value={subTenant}
                    onChange={setSubTenant}
                    label="Client (sub-tenant)"
                    helperText="Leave empty to report on the whole project."
                />
                <TextField
                    size="small"
                    label="Recipients"
                    multiline
                    minRows={2}
                    value={recipientsRaw}
                    onChange={e => setRecipientsRaw(e.target.value)}
                    error={invalid.length > 0 || tooMany}
                    helperText={
                        invalid.length > 0
                            ? `Not an email: ${invalid.join(', ')}`
                            : tooMany
                              ? `Your plan allows ${maxRecipients} recipients per report.`
                              : `Comma or newline separated${maxRecipients !== null ? ` (max ${maxRecipients})` : ''}. Every email has an unsubscribe link.`
                    }
                />
                <Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={csvAllowed && includeCsv}
                                disabled={!csvAllowed}
                                onChange={e => setIncludeCsv(e.target.checked)}
                            />
                        }
                        label="Attach CSV"
                    />
                    {!csvAllowed && (
                        <FormHelperText sx={{ mt: -0.5 }}>
                            CSV attachments need a paid plan.
                        </FormHelperText>
                    )}
                </Box>
                <FormControlLabel
                    control={
                        <Switch checked={active} onChange={e => setActive(e.target.checked)} />
                    }
                    label={active ? 'Active' : 'Paused'}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!valid || busy}
                    onClick={() => {
                        void save()
                    }}
                >
                    {report ? 'Save' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function PreviewDialog({
    report,
    result,
    onClose,
}: {
    report: ScheduledReport
    result: DigestRunResult
    onClose: () => void
}) {
    const preview = result.preview
    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Preview · {report.name || 'Scheduled report'}
                <Typography variant="body2" color="text.secondary">
                    Period {result.period.from} – {result.period.to}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {preview ? (
                    <>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Subject:</strong> {preview.subject}
                        </Typography>
                        <Box
                            component="iframe"
                            title="Email preview"
                            sandbox=""
                            srcDoc={preview.html}
                            sx={{
                                width: '100%',
                                height: 560,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: '#fff',
                            }}
                        />
                    </>
                ) : (
                    <Alert severity="info">Nothing to preview ({result.status}).</Alert>
                )}
            </DialogContent>
            <DialogActions>
                {preview?.csv && (
                    <Button
                        startIcon={<Download />}
                        onClick={() =>
                            downloadText(
                                digestCsvFilename(report.name, result.period),
                                preview.csv ?? '',
                            )
                        }
                    >
                        Download CSV
                    </Button>
                )}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

export function ScheduledReportsSection({
    portals,
    selectedPortalId,
    ent,
}: {
    portals: Portal[]
    /** Pre-selected branding for new schedules. */
    selectedPortalId: string | null
    ent: Entitlements
}) {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const { data = [], isLoading, error } = useListScheduledReportsQuery(projectId)
    const [update, updateState] = useUpdateScheduledReportMutation()
    const [remove, removeState] = useDeleteScheduledReportMutation()
    const [send, sendState] = useSendScheduledReportMutation()
    const [editing, setEditing] = useState<ScheduledReport | 'new' | null>(null)
    const [preview, setPreview] = useState<{
        report: ScheduledReport
        result: DigestRunResult
    } | null>(null)
    const [toSend, setToSend] = useState<ScheduledReport | null>(null)
    const [toDelete, setToDelete] = useState<ScheduledReport | null>(null)
    const portalName = new Map(portals.map(p => [p.id, p.branding.productName]))
    const limit = ent?.maxScheduledReports ?? null

    const runPreview = async (r: ScheduledReport) => {
        try {
            const result = await send({ projectId, id: r.id, dryRun: true }).unwrap()
            setPreview({ report: r, result })
        } catch {
            // shown below
        }
    }

    const sendNow = async (r: ScheduledReport) => {
        try {
            const res = await send({ projectId, id: r.id }).unwrap()
            dispatch(showNotification(sendResultMessage(res)))
        } catch {
            // shown below
        }
    }

    return (
        <SectionCard
            title="Scheduled reports"
            action={
                <Button startIcon={<Add />} onClick={() => setEditing('new')} disabled={!!error}>
                    New schedule
                </Button>
            }
        >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Weekly or monthly email digests of a client's (or the whole project's) KPIs,
                optionally branded with a portal.
                {limit !== null
                    ? ` Your plan allows ${limit} schedule${limit === 1 ? '' : 's'} per project.`
                    : ''}
            </Typography>
            <ApiErrorAlert
                error={updateState.error ?? removeState.error ?? sendState.error}
                sx={{ mb: 2 }}
            />
            {error ? (
                <ApiErrorAlert error={error} />
            ) : (
                <ReportStatus
                    isLoading={isLoading}
                    isEmpty={data.length === 0}
                    emptyTitle="No scheduled reports"
                    emptyDescription="Send your clients a digest every Monday or on the 1st of the month."
                >
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Report</TableCell>
                                    <TableCell>Scope</TableCell>
                                    <TableCell align="right">Recipients</TableCell>
                                    <TableCell>Next run</TableCell>
                                    <TableCell>Last run</TableCell>
                                    <TableCell>Active</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map(r => (
                                    <TableRow key={r.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {r.name || '(unnamed)'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {r.frequency}
                                                {r.include_csv ? ' · CSV' : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {r.sub_tenant ?? 'Whole project'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {r.portal_id
                                                    ? (portalName.get(r.portal_id) ?? 'Portal')
                                                    : 'No portal'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={r.recipients.join(', ')}>
                                                <span>{r.recipients.length}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {r.active && r.next_run_at
                                                ? fmtDateTime(r.next_run_at)
                                                : '—'}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {r.last_run_at ? (
                                                <Tooltip title={r.last_error ?? ''}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {fmtDateTime(r.last_run_at)}
                                                        {r.last_status && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label={r.last_status}
                                                                color={lastRunColor(r.last_status)}
                                                            />
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                size="small"
                                                checked={r.active}
                                                onChange={e => {
                                                    void update({
                                                        projectId,
                                                        id: r.id,
                                                        active: e.target.checked,
                                                    })
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Preview">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Preview"
                                                        disabled={sendState.isLoading}
                                                        onClick={() => {
                                                            void runPreview(r)
                                                        }}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Send now">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Send now"
                                                        disabled={sendState.isLoading}
                                                        onClick={() => setToSend(r)}
                                                    >
                                                        <Send fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setEditing(r)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setToDelete(r)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </ReportStatus>
            )}

            {editing && (
                <ScheduleDialog
                    report={editing === 'new' ? null : editing}
                    portals={portals}
                    defaultPortalId={selectedPortalId}
                    ent={ent}
                    onClose={() => setEditing(null)}
                />
            )}
            {preview && (
                <PreviewDialog
                    report={preview.report}
                    result={preview.result}
                    onClose={() => setPreview(null)}
                />
            )}
            <ConfirmDialog
                open={!!toSend}
                title="Send now?"
                message={`Emails the last completed ${toSend?.frequency === 'monthly' ? 'month' : 'week'} to ${toSend?.recipients.length ?? 0} recipient(s). The regular schedule is unchanged.`}
                confirmLabel="Send"
                onConfirm={() => {
                    if (toSend) {
                        void sendNow(toSend)
                    }
                    setToSend(null)
                }}
                onCancel={() => setToSend(null)}
            />
            <ConfirmDialog
                open={!!toDelete}
                title="Delete scheduled report?"
                message={`"${toDelete?.name || 'This report'}" stops being sent.`}
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void remove({ projectId, id: toDelete.id })
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </SectionCard>
    )
}
